import { computed, inject, Injectable, signal } from '@angular/core';
import { UserService } from '../user/user.service';
import { User } from '../user/user.types';
import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from '@microsoft/signalr';
import { first, tap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import {
	AccountClient,
	AccountTierEnum,
	MultiTenantInviteDto,
} from './api.service';
import { NotificationService } from './notification/notification.service';
import { Router } from '@angular/router';
import { MultiTenantStore } from 'app/store/multi-tenant.store';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
	providedIn: 'root',
})
export class MultiTenantService {
	private readonly _userService = inject(UserService);
	private readonly _notificationService = inject(NotificationService);
	private readonly _router = inject(Router);
	private readonly _accountClient = inject(AccountClient);
	private readonly _multiTenantStore = inject(MultiTenantStore);
	private readonly _auth0 = inject(AuthService);

	private hubConnection: HubConnection;

	isEnterprise = computed(
		() =>
			this._userService.userSig$()?.accountTier ===
			AccountTierEnum.Enterprise
	);

	async init() {
		await this.startSignalRConnection();
		this.setupSignalRListeners();
	}

	private async startSignalRConnection(): Promise<void> {
		try {
			this.hubConnection = new HubConnectionBuilder()
				.withUrl(`${environment.apiUrl}/hubs/multiTenant`, {
					accessTokenFactory: () =>
						firstValueFrom(this._auth0.getAccessTokenSilently()),
				})
				.withAutomaticReconnect()
				.withStatefulReconnect()
				.configureLogging(
					environment.env == 'dev' ? LogLevel.None : LogLevel.Trace
				)
				.build();

			await this.hubConnection
				.start()
				.catch((err) =>
					console.error('Error establishing connection: ' + err)
				);
		} catch (err) {
			console.error('Error establishing connection: ' + err);
		}
	}

	private setupSignalRListeners(): void {
		const userId = this._userService.userSig$()?.id;

		this.hubConnection.on(
			`InviteCollaborator_${userId}`,
			(invite: MultiTenantInviteDto) => {
				this.showNotification(invite);
			}
		);
	}

	private showNotification(invite: MultiTenantInviteDto) {
		let title = 'Invite to collaborate';
		let actions = [
			{
				text: 'Accept',
				command: () => this.onAcceptInvite(invite.id),
			},
			{
				text: 'Reject',
				command: () =>
					this.onRejectInvite(invite.id, invite.customerId),
			},
		];

		let snackbarRef = this._notificationService.open({
			title,
			subtitle: `You have been invited to join ${invite.companyName}`,
			type: 'info',
			actions: actions,
		});
	}

	onAcceptInvite(inviteId: string): void {
		if (!this.isEnterprise()) {
			this._notificationService.open({
				title: 'Invite',
				subtitle:
					'Accepting External Invites requires an Enterprise Tier Upgrade',
				type: 'error',
				actions: [
					{
						text: 'Upgrade',
						command: () => {
							this._router.navigate([
								'/settings',
								'plan-billing',
							]);
						},
						variant: 'primary',
					},
				],
			});

			return;
		}

		this._accountClient
			.acceptInvite(inviteId)
			.pipe(
				first(),
				tap(() => this._multiTenantStore.refreshCompanies())
			)
			.subscribe((invites) => {
				this._notificationService.open({
					title: 'Invite',
					subtitle: 'Invite has been accepted!',
					type: 'success',
				});
			});
	}

	onRejectInvite(inviteId: string, customerId: number): void {
		this._accountClient
			.rejectInvite(inviteId, customerId)
			.pipe(first())
			.subscribe({
				next: (invites) => {
					this._notificationService.open({
						title: 'Invite',
						subtitle: 'Invite has been rejected!',
						type: 'success',
					});
				},
			});
	}
}
